import React from 'react'
import {
  FaqQuestion,
  type HelpDrawerFaqItems,
} from '../../../../components/AddEditProductFormComponents/FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'
import { NoteV2 } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLightbulb } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'
import type { MicrosoftFormFAQsClasses } from './MicrosoftFormFAQs.styles'
import { MicrosoftFormFAQsItem } from './MicrosoftFormFAQs.types'

const TABLE_DATA = [
  { key: 'Key', value: 'Value' },
  { key: 'Region', value: 'North America' },
  { key: 'Sales Lead', value: 'John Doe' },
  { key: 'FY', value: 'FY25' },
] as const

function getAuthorizationDetailFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion
          section=""
          question="What should I enter as the Buyer name?"
        />
      ),
      answer: (
        <div>
          Enter the legal name of the buyer who will receive the offer. This
          should match the name associated with their Microsoft billing account.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Where can I find the Buyer Billing Account ID?"
        />
      ),
      answer: (
        <div>
          This is the unique ID associated with the buyer's Microsoft billing
          account. The buyer can find this in their Microsoft Partner Center
          under their billing profile. Please verify with the buyer if unsure.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What format should the Buyer Billing Account ID be in?"
        />
      ),
      answer: (
        <div>
          The Billing Account ID is a numeric string typically 9 to 12 digits
          long (e.g., 123456789). Ensure there are no extra spaces or
          characters. You can confirm this with the buyer's Microsoft admin.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What should I enter as the Offer name?"
        />
      ),
      answer: (
        <div>
          <div>This is the name of the offer that will be visible to:</div>
          <ul className={classes.listItems}>
            <li>You (the offer creator)</li>
            <li>The reseller</li>
            <li>The end buyer when the offer is sent via Microsoft</li>
          </ul>
          <div className={classes.noteWrapper}>
            <NoteV2
              description={
                <div className={classes.note}>
                  Choose a name that is clear and easy to identify for all
                  parties involved.
                  <br />
                  Tip: Use a format like _BuyerName_Month_Year_DealType_ (e.g.,
                  Contoso_July2025_ResellerDiscount) for clarity.
                </div>
              }
            />
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What happens after the Authorization expiration date?"
        />
      ),
      answer: (
        <div>
          The buyer will no longer be able to accept the offer after this date.
          Make sure to set a realistic expiration timeline based on deal stage
          and internal approvals.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Is the Offer description mandatory? What should I write here?"
        />
      ),
      answer: (
        <div>
          No, this field is optional. You can use it to briefly describe the
          offer's purpose, special pricing notes, or any buyer-specific context.
          This is visible to the buyer.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="Who should I add as recipients?" />
      ),
      answer: (
        <div>
          Recipients should be the decision-makers or authorized individuals on
          the buyer side who can review and accept the offer in Microsoft
          Partner Center. You can add multiple people here.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What is the purpose of the Custom Email Message?"
        />
      ),
      answer: (
        <div>
          This message is sent as part of the email from Labra when notifying
          recipients. Use it to personalize your communication or add
          instructions.
        </div>
      ),
    },
  ]
}

function getResellerInfoFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion
          section=""
          question="What should I enter as the Reseller name?"
        />
      ),
      answer: (
        <div>
          Enter the official name of the reseller organization that will be
          facilitating the offer. This name should match the one registered with
          Microsoft Partner Center.
          <div className={classes.noteWrapper2}>
            <NoteV2
              description={
                <div className={classes.tipNote}>
                  <div className={classes.noteIcon}>
                    <FontAwesomeIcon
                      icon={faLightbulb}
                      style={{ width: '13px', height: '17px' }}
                    />
                  </div>
                  <div className={classes.noteTitle}>
                    Tip:
                    <span className={classes.noteDescription}>
                      This is visible to the buyer and should clearly identify
                      the reseller.
                    </span>
                  </div>
                </div>
              }
            />
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Where can I find the Reseller Account ID?"
        />
      ),
      answer: (
        <div>
          The Reseller Account ID is the{' '}
          <span className={classes.boldText}>
            Microsoft Partner Center Account ID
          </span>{' '}
          for the reseller. You can find it in the{' '}
          <span className={classes.boldText}>
            Microsoft Partner Center {'>'} Account Settings {'>'} Legal info
            section.
          </span>
          <div className={classes.noteWrapper2}>
            <NoteV2
              description={
                <div className={classes.tipNote}>
                  <div className={classes.noteIcon}>
                    <FontAwesomeIcon
                      icon={faLightbulb}
                      style={{ width: '15px', height: '16px' }}
                    />
                  </div>
                  <div className={classes.noteTitle}>
                    {' '}
                    Note:
                    <span className={classes.noteDescription}>
                      Make sure the account is active and authorized to transact
                      on the buyer's behalf.
                    </span>
                  </div>
                </div>
              }
            />
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="Can I use any account ID here?" />
      ),
      answer: (
        <div>
          No. You must use a valid and registered Microsoft Reseller ID. Invalid
          or inactive accounts may result in offer rejection by Microsoft.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Is this information visible to the buyer?"
        />
      ),
      answer: (
        <div>
          Yes. The <span className={classes.boldText}>Reseller Name</span> will
          be shown in the buyer's view of the offer. The{' '}
          <span className={classes.boldText}>Account ID</span> is not shown but
          is used for Microsoft backend validation
        </div>
      ),
    },
  ]
}

function getContractDurationFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion section="" question="How does Contract Duration work?" />
      ),
      answer: (
        <div>
          <div>
            Contract duration defines the overall length of the agreement
            between the reseller and the buyer. It begins on the first day of
            the selected start month and ends on the last day of the selected
            end month.
          </div>
          <div>
            For example, if you select Jan 2025 to Mar 2025, the contract runs
            from Jan 1 to Mar 31, 2025.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What's the difference between Start Date and Start Month?"
        />
      ),
      answer: (
        <div>
          Contract Start Date: Choose between two options:
          <ul className={classes.listItems}>
            <li>
              Accepted Date: Starts from the day the buyer accepts the offer
            </li>
            <li>
              Specified Date: You define the exact month when the contract
              starts
            </li>
          </ul>
          Contract Start Month: Only appears when Specified Date is selected.Use
          Specified Date for scheduled launches or fixed timelines.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="Why is the end month mandatory?" />
      ),
      answer: (
        <div>
          Microsoft requires a clear end date for all contracts. This ensures
          proper billing and renewal configuration.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Can I set a partial month duration?"
        />
      ),
      answer: (
        <div>
          No. Contracts always start on the first of the month and end on the
          last day of the month. Mid-month start or end dates are not supported.
        </div>
      ),
    },
  ]
}

function getRenewalFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion
          section=""
          question={'What does "Renewal" mean in this context?'}
        />
      ),
      answer: (
        <div>
          <div>
            This option is used to indicate whether the current offer is meant
            to renew an existing paid contract — either within Microsoft
            Commercial Marketplace or through an off-platform/private agreement.
          </div>
          <div>
            Selecting "Yes" flags this offer as a continuation or extension of
            an existing arrangement.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question={'What happens if I select "Yes"?'} />
      ),
      answer: (
        <div>
          The buyer and Microsoft will understand this is a renewal, not a
          brand-new deal. It may influence billing alignment, reporting, or
          buyer experience on Microsoft's side.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question={'When should I select "No"?'} />
      ),
      answer: (
        <div>
          Choose No if:
          <ul className={classes.listItems}>
            <li>This is a net-new buyer or</li>
            <li>
              You're creating an entirely new contract, not tied to any existing
              paid agreement.
            </li>
          </ul>
        </div>
      ),
    },
  ]
}

function getPricingFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion
          section=""
          question={'What is the difference between "Absolute" and "Discount"?'}
        />
      ),
      answer: (
        <div>
          <div>
            Absolute: You directly define the total contract value (e.g.,
            $30,750).
          </div>
          <div>
            Discount: You apply a percentage discount on existing plan prices.
          </div>
          <div>
            Choose Absolute for fixed-price deals and Discount when the price is
            derived from a standard rate card.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Can I create multi-party offers with both options?"
        />
      ),
      answer: (
        <div>
          No, you can create multi-party offers, but only with either Absolute
          or Discount, not both in the same offer.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question={'What is a "Plan" and how do I select one?'}
        />
      ),
      answer: (
        <div>
          <div>
            Plans are pre-defined offer structures created for your product
            (e.g., Basic, Pro, Enterprise).
          </div>
          <div>
            Select a plan from the dropdown — the description will auto-populate
            based on the selected plan.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question={'What is "Contract duration" inside a plan?'}
        />
      ),
      answer: (
        <div>
          <div>
            This defines the billing term for the selected plan (e.g., 1 month,
            12 months).
          </div>
          <div>
            Each plan may support multiple durations. Choose the appropriate one
            you want to offer to the buyer.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question={"Why can't I add an installment schedule?"}
        />
      ),
      answer: (
        <div>
          <div>
            You'll only see the installment schedule option when both of the
            following conditions are met:
          </div>
          <ul className={classes.listItems}>
            <li>You've selected Absolute pricing (not Discount), and</li>
            <li>The contract duration is 12 months or more</li>
          </ul>
          <div>
            If either of these is not selected, the Flexible Schedule and
            installment options will be hidden.
          </div>
          <div>
            To enable installments, switch to Absolute pricing and choose a
            contract duration of 12 months or more.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="How does the Installment Schedule work?"
        />
      ),
      answer: (
        <div>
          <div>The details of the working of installment schedule:</div>
          <ol className={classes.listItems}>
            <li>
              First invoice date is auto-set to contract start date (1st of the
              start month or the accepted date) and cannot be edited.
            </li>
            <li>
              You can choose how the remaining payments are spread — monthly,
              quarterly, yearly, or custom.
            </li>
            <li>
              Installment amounts must be:
              <ol type="a">
                <li>≥ 0</li>
                <li>≤ 100,000,000</li>
                <li>Up to 5 decimal places</li>
              </ol>
            </li>
            <li>You are allowed to add a maximum of 70 installments. </li>
            <li>
              Invoice date cannot be set after contract end date i.e. all
              installments must be taken during the contract period.
            </li>
          </ol>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="What are Metering Dimensions?" />
      ),
      answer: (
        <div>
          If your product is usage-based (e.g., data transfer, compute hours),
          you can define base quantities to offer the end buyer for predefined
          dimensions that are a part of the chosen plan.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="What are Min-Max Users used for?" />
      ),
      answer: (
        <div>
          <div>If applicable, define user constraints:</div>
          <ul className={classes.listItems}>
            <li>Minimum number of users required to activate the offer</li>
            <li>Maximum number of users supported in the selected plan</li>
          </ul>
        </div>
      ),
    },
    {
      question: <FaqQuestion section="" question="Can I add multiple plans?" />,
      answer: (
        <div>
          <div>Yes, you can add more plans using "Add Plan".</div>
          <div>
            Each plan can have its own billing term, frequency, and pricing
            logic.
          </div>
        </div>
      ),
    },
  ]
}

function getEulaFaqs(classes: MicrosoftFormFAQsClasses): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion
          section=""
          question="What is an EULA and why do I need one?"
        />
      ),
      answer: (
        <div>
          <div>
            EULA (End-user license agreement) defines the legal terms under
            which your product is offered to the buyer.
          </div>
          <div>
            Microsoft requires every offer to have clear legal terms, either
            through standard Marketplace contracts or your own custom terms.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question=" What are the available options for EULA configuration?"
        />
      ),
      answer: (
        <div>
          <div>You can choose one of the following:</div>
          <ol className={classes.listItems}>
            <li>Public offer legal terms</li>
            <ul className={clsx(classes.listItems, classes.sublistItems)}>
              <li>
                These are your standard product terms already published in your
                public listing on Microsoft Marketplace.
              </li>
              <li>
                Use this if your reseller offer terms are identical to your
                public offer.
              </li>
            </ul>
            <li>Standard Contract for Microsoft Marketplace</li>
            <ul className={clsx(classes.listItems, classes.sublistItems)}>
              <li>
                This is Microsoft's standard legal template for transactions.
              </li>
              <li>
                Use this if you want to rely on Microsoft's default agreement
                and don't need custom legal terms.
              </li>
            </ul>
            <li>Custom EULA</li>
            <ul className={clsx(classes.listItems, classes.sublistItems)}>
              <li>
                Upload your own EULA(s) in PDF format. You can upload up to 5
                PDF files.
              </li>
              <li>
                Use this if your deal requires custom or buyer-specific terms.
              </li>
            </ul>
          </ol>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="How many files can I upload for a Custom EULA?"
        />
      ),
      answer: (
        <div>
          You can upload up to 5 PDF files, which may include multiple documents
          (e.g., license terms, privacy policy, addendums).
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="Can I skip uploading a EULA?" />
      ),
      answer: (
        <div>
          No. At least one legal configuration option is mandatory for all
          offers.
        </div>
      ),
    },
  ]
}

function getStakeholderFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion
          section=""
          question="What is the purpose of this section?"
        />
      ),
      answer: (
        <div>
          <div>
            This section allows you to add internal stakeholders who should
            receive notifications related to this offer — such as submission,
            acceptance, or changes.
          </div>
          <div>
            Notifications will be sent to both internal Labra users and any
            custom emails you add.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="Who can I add as stakeholders?" />
      ),
      answer: (
        <div>
          <ol className={classes.listItems}>
            <li>Team members with Labra access:</li>
            <ul className={clsx(classes.listItems, classes.sublistItems)}>
              <li>
                You can select users already added to your Labra account. These
                are typically your colleagues who manage marketplace deals.
              </li>
            </ul>
            <li>Custom email addresses:</li>
            <ul className={clsx(classes.listItems, classes.sublistItems)}>
              <li>
                You can also add other stakeholders of your team (e.g., sales
                reps, finance team, legal) who don't have a Labra login but
                still need to be kept in the loop.
              </li>
              <li>
                These custom emails are optional but useful for keeping
                non-platform users informed.
              </li>
            </ul>
          </ol>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="How do I add multiple custom emails?"
        />
      ),
      answer: (
        <div>
          <div>
            Enter the email address and press Enter or Comma after each one.
          </div>
          <div>You can add multiple email addresses in one go.</div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Are these stakeholders involved in offer approvals?"
        />
      ),
      answer: (
        <div>
          No, stakeholders only receive notifications — they cannot modify or
          approve offers.
        </div>
      ),
    },
  ]
}

function getMetaDataFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion section="" question="What is Custom Metadata used for?" />
      ),
      answer: (
        <div>
          <div>
            Custom metadata lets you attach key-value pairs to this offer for
            your own internal tracking, grouping, or reporting purposes.
          </div>
          <div>
            Example: You could tag offers by region, sales rep, campaign ID,
            etc.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Is this metadata visible to the buyer or Microsoft?"
        />
      ),
      answer: (
        <div>
          No. Custom metadata is used only within your Labra workspace and is
          not visible externally to buyers or Microsoft.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="What kind of values can I add?" />
      ),
      answer: (
        <div>
          <div>
            You can add anything meaningful to your organization — such as:
          </div>
          <div className={classes.tableWrapper}>
            {TABLE_DATA.map((row, index) => (
              <div key={index}>
                <div className={classes.tableRow}>
                  <div className={classes.tableCell}>
                    <span className={classes.tableText}>{row.key}</span>
                  </div>
                  <div className={classes.tableCell}>
                    <span className={classes.tableText}>{row.value}</span>
                  </div>
                </div>
                {index < TABLE_DATA.length - 1 && (
                  <div className={classes.tableDivider} />
                )}
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Can I add multiple metadata entries?"
        />
      ),
      answer: (
        <div>
          Yes, click "Add more custom metadata" to enter as many key-value pairs
          as needed.
        </div>
      ),
    },
  ]
}

function getBuyerNotificationFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion
          section=""
          question="Why am I being asked for buyer details?"
        />
      ),
      answer: (
        <div>
          Labra uses these details to send reminder emails nudging the buyer to
          complete offer acceptance, purchase, and account configuration steps
          before the offer expires.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What happens if I don't fill this section?"
        />
      ),
      answer: (
        <div>
          No reminder emails will be sent to the buyer. You'll need to follow up
          manually.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Will reminders be sent immediately after offer creation?"
        />
      ),
      answer: (
        <div>
          <div>
            No, reminders are timed based on the offer expiry window and buyer
            progress.{' '}
          </div>
          <div>
            For example, they may be triggered if any steps remain incomplete
            closer to expiry.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What if the buyer has already completed all steps?"
        />
      ),
      answer: (
        <div>
          Labra tracks offer progress. If all steps are done, no reminders will
          be sent—even if details were provided.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="Can I update buyer info later?" />
      ),
      answer: (
        <div>
          Yes, if you skip this step during offer creation, you can still add or
          update buyer details later from the Reseller authorization (MPPO)
          details page.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="What information is required?" />
      ),
      answer: (
        <div>
          First name, last name, email, and title (optional) are necessary for
          sending reminders.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="How is buyer data handled?" />
      ),
      answer: (
        <div>
          Labra only uses this information to send reminders related to the
          offer. Data is securely stored and not used for any other purpose.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What if I entered incorrect buyer info?"
        />
      ),
      answer: (
        <div>
          You can update buyer details from the Reseller authorization (MPPO)
          details page anytime before the offer expires.
        </div>
      ),
    },
  ]
}

function getPurchaseTermsFaqs(
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  return [
    {
      question: (
        <FaqQuestion
          section=""
          question={'What does "Purchase Start Month" mean?'}
        />
      ),
      answer: (
        <div>
          <div>
            This determines when the buyer can begin purchasing under the
            private pricing
          </div>
          <ul className={classes.listItems}>
            <li>
              If you select{' '}
              <span className={classes.boldText}>Accepted Date</span>, the buyer
              can purchase as soon as they accept the offer.
            </li>
            <li>
              If you select{' '}
              <span className={classes.boldText}>Specific Month</span>, the
              buyer can purchase starting from the 1st day of that month (UTC).
              Purchases before this date will not be possible.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question={'What does "Purchase End Month" mean?'}
        />
      ),
      answer: (
        <div>
          <div>
            This determines when the buyer can begin purchasing under the
            private pricing
          </div>
          <ul className={classes.listItems}>
            <li>
              If you select{' '}
              <span className={classes.boldText}>Accepted Date</span>, the buyer
              can purchase as soon as they accept the offer.
            </li>
            <li>
              If you select{' '}
              <span className={classes.boldText}>Specific Month</span>, the
              buyer can purchase starting from the 1st day of that month (UTC).
              Purchases before this date will not be possible.
            </li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What if the buyer accepts the offer before offer expiry, but the purchase start date is in the future?"
        />
      ),
      answer: (
        <div>
          The buyer will not be able to make a purchase until the start month
          begins. The offer will remain in "accepted" state but unavailable for
          purchase until the pricing becomes active.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Can I set a start month in the past?"
        />
      ),
      answer: (
        <div>
          <span className={classes.boldText}>No.</span> The start month must be
          the current or a future month.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What happens if I select Accepted Date for a pay-as-you-go product?"
        />
      ),
      answer: (
        <div>
          The private pricing will apply for the{' '}
          <span className={classes.boldText}>entire calendar month</span> in
          which the offer is accepted even if it's accepted at the end of the
          month.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="Can I change these dates later?" />
      ),
      answer: (
        <div>
          <span className={classes.boldText}>No.</span> Once the offer is
          published, start and end months cannot be edited. Please double-check
          before submitting.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Does the start or end month impact billing cycles?"
        />
      ),
      answer: (
        <div>
          Microsoft billing and metering will follow the standard Marketplace
          behaviour. These fields only control when the private pricing becomes
          available and when it expires.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Is there a minimum or maximum duration between start and end month?"
        />
      ),
      answer: (
        <div>
          Microsoft doesn't impose a hard limit, but best practice is to ensure
          a reasonable window (e.g. 1–6 months) that gives your buyer time to
          complete the steps and purchase.
        </div>
      ),
    },
  ]
}

export function getFaqItemsForItem(
  item: MicrosoftFormFAQsItem | undefined,
  classes: MicrosoftFormFAQsClasses
): HelpDrawerFaqItems[] {
  if (item === undefined) return []
  switch (item) {
    case MicrosoftFormFAQsItem.AUTHORIZATION_DETAIL:
      return getAuthorizationDetailFaqs(classes)
    case MicrosoftFormFAQsItem.RESALE_INFO:
      return getResellerInfoFaqs(classes)
    case MicrosoftFormFAQsItem.CONTRACT_DURATION:
      return getContractDurationFaqs(classes)
    case MicrosoftFormFAQsItem.RENEWAL:
      return getRenewalFaqs(classes)
    case MicrosoftFormFAQsItem.PRICING:
      return getPricingFaqs(classes)
    case MicrosoftFormFAQsItem.META_DATA:
      return getMetaDataFaqs(classes)
    case MicrosoftFormFAQsItem.BUYER_NOTIFICATION:
      return getBuyerNotificationFaqs(classes)
    case MicrosoftFormFAQsItem.PURCHASE_TERMS:
      return getPurchaseTermsFaqs(classes)
    case MicrosoftFormFAQsItem.STAKEHOLDERS:
      return getStakeholderFaqs(classes)
    case MicrosoftFormFAQsItem.EULA:
      return getEulaFaqs(classes)
    default:
      return []
  }
}
